<template>
  <v-container>
    <v-row justify-center>
      <v-col>
        <v-card>
          <v-app-bar color="#0F1547">
            <!-- Federation selection -->
            <v-select
              v-if="availableFederations && availableFederations.length > 1 && selectedCoach === null && selectedDiscipline === null"
              v-model="selectedFederation"
              item-text="name"
              :items="availableFederations"
              v-on:change="selectedFederationChanged"
              return-object
              clearable
              label="Fédération"
              class="v-select-cust"
              dense
              hide-details="auto"
              solo
              solo-inverted
              flat
              dark
            ></v-select>

            <!-- Discipline selection -->
            <v-select
              v-if="availableDisciplines && availableDisciplines.length > 1 && selectedCoach === null && selectedFederation === null"
              v-model="selectedDiscipline"
              item-text="name"
              :items="availableDisciplines"
              v-on:change="selectedDisciplineChanged"
              return-object
              clearable
              label="Discipline"
              class="v-select-cust"
              dense
              hide-details="auto"
              solo
              solo-inverted
              flat
              dark
            ></v-select>

            <!-- Coach selection -->
            <v-select
              v-if="availableCoaches && availableCoaches.length > 1 && selectedDiscipline === null && selectedFederation === null"
              v-model="selectedCoach"
              item-text="email"
              :items="availableCoaches"
              v-on:change="selectedCoachChanged"
              return-object
              clearable
              label="Entraîneur"
              class="v-select-cust"
              dense
              hide-details="auto"
              solo
              solo-inverted
              flat
              dark
            ></v-select>

            <!-- Table title -->
            <v-toolbar-title style="color: white">Exercices</v-toolbar-title>

            <v-spacer></v-spacer>

            <!-- Create button -->
            <v-btn to="exercise" style="color: #0f1547" color="#fff" v-if="canManageExercises">
              <v-icon>content_paste</v-icon>
              &nbsp; Créer un exercice
            </v-btn>
          </v-app-bar>

          <div>
            <v-data-table
              :headers="_headers"
              :items="exercices"
              item-key="id"
              :options.sync="options"
              :server-items-length="totalExercices"
              :footer-props="{
                'items-per-page-options': pageSizes,
              }"
              :items-per-page="pageSize"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              @click:row="emitter"
            >
              <!-- Filters params -->
              <template v-slot:[`body.prepend`]>
                <tr>
                  <!-- empty -->
                  <td></td>
                  <!-- id -->
                  <td>
                    <v-text-field v-model="searchParams.id" type="number" label="Id" @input="isTyping = true" clearable @click:clear="onClearIdClicked()"></v-text-field>
                  </td>
                  <!-- title -->
                  <td>
                    <v-text-field
                      v-model="searchParams.title"
                      type="text"
                      label="Titre"
                      @input="isTyping = true"
                      clearable
                      @click:clear="onClearTitleClicked()"
                    ></v-text-field>
                  </td>
                  <!-- Destination -->
                  <td>
                    <v-select
                      v-model="searchParams.destinationType"
                      :items="destinationTypeItems"
                      Destination
                      label="Destination"
                      @input="isTyping = true"
                      clearable
                      @click:clear="onClearDestinationClicked()"
                    ></v-select>
                  </td>
                  <!-- user -->
                  <td>
                    <v-text-field
                      v-model="searchParams.user"
                      type="text"
                      label="Créateur"
                      @input="isTyping = true"
                      clearable
                      @click:clear="onClearUserClicked()"
                    ></v-text-field>
                  </td>
                  <!-- type -->
                  <td>
                    <v-select
                      v-model="searchParams.exerciseType"
                      :items="exerciseTypeItems"
                      Type
                      label="Type"
                      @input="isTyping = true"
                      clearable
                      @click:clear="onClearTypeClicked()"
                    ></v-select>
                  </td>
                  <!-- workform -->
                  <td v-if="_isWorkFormColumnVisible">
                    <v-select
                      v-model="searchParams.workForms"
                      :items="allWorkFormsForDiscipline"
                      item-text="title"
                      return-object
                      label="Forme de travail"
                      @input="isTyping = true"
                      clearable
                      multiple
                      @click:clear="onClearWorkFormClicked()"
                    ></v-select>
                  </td>
                  <td></td>
                </tr>
              </template>

              <!-- Illustration -->
              <template v-slot:[`item.preview`]="{ item }">
                <!-- Picture Preview -->
                <v-img v-if="item.preview" height="auto" max-width="250" :src="item.preview"></v-img>
                <!-- Image -->
                <v-img
                  v-else-if="item.firstMedia && (item.firstMedia.image || item.firstMedia.isImage) && !item.firstMedia.src && !item.firstMedia.videoOptions"
                  height="auto"
                  max-width="250"
                  :src="item.firstMedia.image"
                ></v-img>
                <!-- Video -->
                <video-player
                  v-else-if="item.firstMedia && (item.firstMedia.videoOptions || item.firstMedia.youtube)"
                  style="height: auto; width: 250px; margin: 0"
                  :options="item.firstMedia.videoOptions"
                ></video-player>
                <!-- IFrame -->
                <iframe v-else-if="item.firstMedia && item.firstMedia.canal" style="height: auto; width: 250px; margin: 0" :src="item.firstMedia.src"></iframe>
                <!-- Link -->
                <a v-else-if="item.firstMedia && item.firstMedia.src" :href="item.firstMedia.src" target="_blank">{{ item.firstMedia.src }}</a>
              </template>

              <!-- DDL Destination Type -->
              <template v-slot:[`item.destinationType`]="{ item }">
                {{ destinationTypeItems.find((e) => e.value === item.destinationType).text }}
              </template>

              <!-- DDL Exercise Type -->
              <template v-slot:[`item.exerciseType`]="{ item }">
                {{ exerciseTypeItems.find((e) => e.value === item.exerciseType).text }}
              </template>

              <!-- WorkForms -->
              <template v-slot:[`item.workForms`]="{ item }">
                <v-chip color="primary" v-for="workform in item.workForms" :key="workform.id" style="margin: 1px">
                  {{ workform.title }}
                </v-chip>
              </template>

              <!-- Actions -->
              <template v-slot:[`item.actions`]="{ item }">
                <!-- Edit button -->
                <v-btn :to="`/exercise/${item.id}`" class="md-just-icon md-simple md-primary" v-if="canManageExercises" :disabled="!item.canManageExercise">
                  <v-icon>edit</v-icon>
                  <md-tooltip md-direction="top">Editer</md-tooltip>
                </v-btn>

                <!-- View button -->
                <v-btn :to="`/exercise/${item.id}/view`" class="md-simple md-primary">
                  <v-icon>mdi-eye</v-icon>
                  <md-tooltip md-direction="top">Fiche</md-tooltip>
                </v-btn>

                <!-- duplicate button -->
                <v-btn v-on:click="(e) => duplicateExercise(e, item.id)" class="md-just-icon md-simple md-primary" v-if="canManageExercises">
                  <v-icon>mdi-plus-circle-multiple-outline</v-icon>
                  <md-tooltip md-direction="top">Dupliquer</md-tooltip>
                </v-btn>

                <!-- delete button -->
                <v-btn
                  v-on:click="(e) => deleteExercise(e, item.id)"
                  class="md-just-icon md-simple md-primary"
                  v-if="canManageExercises"
                  :disabled="!item.canManageExercise"
                >
                  <v-icon>delete</v-icon>
                  <md-tooltip md-direction="top">Supprimer</md-tooltip>
                </v-btn>
              </template>
            </v-data-table>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { deleteExerciseAsync, getExercisesForCurrentUserAsync, duplicateExerciseByIdAsync } from "@/api/exerciseApi";
import { bus } from "@/main";
import { ExerciceSortType } from "@/models/enums";
import { ExerciseType } from "@/models/enums";
import { DestinationType } from "@/models/enums";
import PermissionsService from "@/services/PermissionsService";
import _ from "lodash";
import store from "@/store";
import AuthService from "@/services/AuthService";
import { getDownloadMediaByPathAsync } from "../../api/mediaApi";
import VideoPlayer from "../../components/VideoPlayer.vue";
import { getAllCurrentUserCoachesAsync } from "@/api/teamApi";
import { getAllCurrentUserFederationsAsync } from "@/api/federationsApi";
import { getAllCurrentUserDisciplinesAsync } from "@/api/disciplinesApi";
import { getWorkFormForDisciplineAsync } from "@/api/workFormApi";

export default {
  name: "exercise-list",

  components: {
    VideoPlayer,
  },

  data() {
    return {
      authService: AuthService,
      // headers
      headers: [
        {
          text: "Image",
          align: "start",
          sortable: false,
          value: "preview",
          show: true,
        },
        {
          text: "id",
          value: "id",
          show: true,
        },
        {
          text: "Titre",
          value: "title",
          show: true,
        },
        {
          text: "Destination",
          value: "destinationType",
          show: true,
        },
        {
          text: "Créateur",
          value: "email",
          show: true,
        },
        {
          text: "Type",
          value: "exerciseType",
          show: true,
        },
        {
          text: "Formes de travail",
          value: "workForms",
          sortable: false,
          show: false,
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          show: true,
        },
      ],
      // Search part ===
      searchParams: {
        id: null,
        title: "",
        destinationType: null,
        user: "",
        exerciseType: "",
        workForms: [],
      },
      destinationTypeItems: [
        { text: "Transversal", value: DestinationType.PUBLIC },
        { text: "Spécifique", value: DestinationType.DISCIPLINE },
        { text: "Fédéral", value: DestinationType.FEDERAL },
        { text: "Entraîneur", value: DestinationType.COACH },
      ],
      exerciseTypeItems: [
        { text: "Exercice", value: ExerciseType.EXERCISE },
        { text: "Test (évaluation)", value: ExerciseType.EVALUATION },
        { text: "Situation de référence", value: ExerciseType.REFERENCE },
      ],
      isTyping: false,
      // Pagination part =======
      totalExercices: 0,
      options: {},
      page: 1,
      pageSize: 5,
      pageSizes: [5, 10, 20],
      userId: null,
      exercices: [],
      // Sort part ======
      sortBy: "id",
      sortDesc: true,
      // Others part ==
      selected: [],
      id: null,
      title: null,
      destinationType: null,
      user: null,
      exerciseType: null,
      lastRequest: null,
      availableFederations: [],
      availableDisciplines: [],
      availableCoaches: [],
      selectedFederation: null,
      selectedDiscipline: null,
      selectedCoach: null,
      allWorkFormsForDiscipline: [],
    };
  },

  watch: {
    options: {
      handler() {
        // Refresh the exercice page with the provided parameters
        this.getExercicePage();
      },
      deep: true,
    },
    "searchParams.id": _.debounce(function () {
      this.isTyping = false;
    }, 1000),
    "searchParams.title": _.debounce(function () {
      this.isTyping = false;
    }, 1000),
    "searchParams.destinationType": _.debounce(function () {
      this.isTyping = false;
    }, 100),
    "searchParams.user": _.debounce(function () {
      this.isTyping = false;
    }, 1000),
    "searchParams.exerciseType": _.debounce(function () {
      this.isTyping = false;
    }, 1000),
    "searchParams.workForms": _.debounce(function () {
      this.isTyping = false;
    }, 1000),
    isTyping: function (value) {
      if (!value) {
        this.onFiltersChanged();
        // Refresh the exercice page with the provided parameters
        this.getExercicePage();
      }
    },
  },

  computed: {
    canViewExercises() {
      // Can view own Exercises
      let canViewOwnExercises = PermissionsService.ExercisesPermissionsService.canViewOwnExercises;
      // Can view all Exercises
      let canViewAllExercises = PermissionsService.ExercisesPermissionsService.canViewAllExercises;
      return canViewOwnExercises || canViewAllExercises;
    },
    canManageExercises() {
      // Can manage own Exercises
      let canManageOwnExercises = PermissionsService.ExercisesPermissionsService.canManageOwnExercises;
      // Can manage all Exercises
      let canManageAllExercises = PermissionsService.ExercisesPermissionsService.canManageAllExercises;
      return canManageOwnExercises || canManageAllExercises;
    },
    _headers() {
      return this.headers.filter((x) => x.show);
    },
    _isWorkFormColumnVisible() {
      let headerWorkForm = this.headers.find((h) => h.value === "workForms");
      if (headerWorkForm) {
        return headerWorkForm.show;
      }
      return false;
    },
  },

  mounted() {
    // Check if the user can see this page
    if (!this.canViewExercises) {
      store.dispatch("setSnackbar", {
        title: `Non Autorisé`,
        text: `Vous n'êtes pas autorisé à consulter cette ressource.`,
        status: "warning",
        timeout: 2000,
      });

      this.$router.replace("/");
    }
  },

  methods: {
    // Build the request params based on page parameters
    getRequestParams(userId, searchParams, page, pageSize, sortBy, sortDesc) {
      let params = {};
      if (userId) {
        params["userId"] = userId;
      }
      if (searchParams) {
        params["searchParams"] = searchParams;
      }
      if (page) {
        params["page"] = page;
      }
      if (pageSize) {
        params["size"] = pageSize;
      }
      if (sortBy) {
        params["sortBy"] = sortBy;
      }
      if (sortDesc) {
        params["sortDesc"] = sortDesc;
      }
      return params;
    },
    // Use request params to call REST API and get the paginated list of exercices
    retrieveExercices() {
      const userId = AuthService.userId;
      this.userId = userId;
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      this.page = page;
      this.pageSize = itemsPerPage;
      this.sortBy = sortBy;
      this.sortDesc = sortDesc;

      // Get the enum value of textual sort Exercice property in order to request the REST API
      var sortByEnum = this.convertExerciceTextPropertiesIntoEnum(sortBy[0]);

      // Get the search params if exists
      let searchParams = {};
      if (this.searchParams) {
        searchParams = {
          id: this.searchParams.id,
          title: this.searchParams.title ? this.searchParams.title.trim().toLowerCase() : "",
          destinationType: this.searchParams.destinationType ? this.searchParams.destinationType : DestinationType.UNDEFINED,
          user: this.searchParams.user ? this.searchParams.user.trim().toLowerCase() : "",
          exerciseType: this.searchParams.exerciseType ? this.searchParams.exerciseType : ExerciseType.UNDEFINED,
          selectedFederationId: this.selectedFederation ? this.selectedFederation.id : null,
          selectedDisciplineId: this.selectedDiscipline ? this.selectedDiscipline.id : null,
          selectedCoachId: this.selectedCoach ? this.selectedCoach.id : null,
          WorkFormIds: this.searchParams.workForms ? this.searchParams.workForms.map((w) => w.id) : null,
        };
      }

      // Build the parameters
      const params = this.getRequestParams(this.userId, searchParams, this.page, this.pageSize, sortByEnum, this.sortDesc);

      // Call the REST API service
      getExercisesForCurrentUserAsync(params)
        .then(async (response) => {
          const { exerciceDatatableItems, totalExercices } = response;
          this.exercices = exerciceDatatableItems;
          this.totalExercices = totalExercices;
          // Get the FirstMedias for each row
          this.exercices = _.cloneDeep(await this.loadFirstMedias());
        })
        .catch((e) => {
          // store.dispatch("setSnackbar", {
          //   title: "Erreur",
          //   text: "La récupération des exercices n'a pas fonctionnée.",
          //   color: "error",
          // });
        });
    },

    // Load the FirstMedias for each received row
    async loadFirstMedias() {
      // Increment the request counter for the loader
      store.dispatch("addRemoveRequest", true);

      const urlCreator = window.URL || window.webkitURL;

      // For each row
      for (let i = 0; i < this.exercices.length; i++) {
        let result;
        let response;
        // If there is a FirstMedia to load
        if (this.exercices[i].firstMedia) {
          switch (this.exercices[i].firstMedia.mediaType) {
            // CASE 1 => IMAGE
            case 1:
              response = await getDownloadMediaByPathAsync(this.exercices[i].firstMedia.url);
              if (response !== undefined) {
                result = await response.blob();
                if (result) {
                  this.exercices[i].firstMedia.image = urlCreator.createObjectURL(result);
                }
              }
              break;
            // CASE 2 => VIDEO
            case 2:
              response = await getDownloadMediaByPathAsync(this.exercices[i].firstMedia.url);
              if (response != undefined) {
                result = await response.blob();
                if (result) {
                  this.exercices[i].firstMedia.image = require("../../assets/Placeholder_video.jpg");
                  this.exercices[i].firstMedia.videoOptions = {
                    techOrder: ["html5", "youtube"],
                    autoplay: false,
                    controls: true,
                    responsive: true,
                    sources: [
                      {
                        src: urlCreator.createObjectURL(result),
                        type: response.headers.get("Content-Type"),
                      },
                    ],
                  };
                }
              }
              break;
            // CASE 3 => OTHER
            case 3:
              // TODO ?
              break;
            // CASE 4 => URL
            case 4:
              this.exercices[i].firstMedia.image = require("../../assets/Placeholder_lien.jpg");
              const parse = require("url-parse");
              const parsed = parse(this.exercices[i].firstMedia.url, true);
              const youtubeRegex = /http(?:s?):\/\/(?:www\.)?youtu(?:be\.com\/watch\?v=|\.be\/)([\w\-\_]*)(&(amp;)?‌​[\w\?‌​=]*)?/gim;
              const pictureRegex = /http[s]?:\/\/.+\.(jpeg|png|jpg|gif)/gim;
              this.exercices[i].firstMedia.src = this.exercices[i].firstMedia.url;
              if (parsed.hostname.includes("canal-sport") && parsed.pathname.endsWith("player")) this.exercices[i].firstMedia.canal = true;
              else if (youtubeRegex.exec(this.exercices[i].firstMedia.url)) {
                this.exercices[i].firstMedia.youtube = true;
                this.exercices[i].firstMedia.videoOptions = {
                  techOrder: ["html5", "youtube"],
                  autoplay: false,
                  controls: true,
                  responsive: true,
                  sources: [
                    {
                      src: this.exercices[i].firstMedia.url,
                      type: "video/youtube",
                    },
                  ],
                };
              } else if (pictureRegex.exec(this.exercices[i].firstMedia.url)) {
                this.exercices[i].firstMedia.isImage = true;
                this.exercices[i].firstMedia.image = this.exercices[i].firstMedia.url;
              }
              break;
          }
        }
      }

      // Decrement the request counter for the loader
      store.dispatch("addRemoveRequest", false);

      return this.exercices;
    },

    // on selected federation changed
    async selectedFederationChanged() {
      this.retrieveExercices();
    },

    // on selected discipline changed
    async selectedDisciplineChanged() {
      // Display / Hide the workform column depending on if a discipline is selected
      let workFormHeaderIndex = this.headers.findIndex((h) => h.value === "workForms");
      if (this.selectedDiscipline) {
        this.headers[workFormHeaderIndex].show = true;

        // get workforms for discipline
        await this.getWorkFormsForDiscipline(this.selectedDiscipline.id);
      } else {
        this.headers[workFormHeaderIndex].show = false;

        // Empty selected workforms
        this.searchParams.workForms = [];
      }

      this.retrieveExercices();
    },

    // on selected coach changed
    async selectedCoachChanged() {
      this.retrieveExercices();
    },

    emitter(value) {
      bus.$emit("reference", value);
    },

    // Duplicate an Exercise
    async duplicateExercise(e, id) {
      e.stopPropagation();

      if (confirm("Etes-vous sûr de vouloir dupliquer cet exercice ?")) {
        var result = await duplicateExerciseByIdAsync(id);

        // If the result is an id > 0, the duplication worked
        if (result && result.id > 0) {
          // Refresh the exercice page with the provided parameters
          this.getExercicePage();

          store.dispatch("setSnackbar", {
            title: "Exercice dupliqué",
            text: "L'exercice a bien été dupliqué.",
            status: "success",
            timeout: 2000,
          });
        }
      }
    },

    // Delete an Exercice
    async deleteExercise(e, id) {
      e.stopPropagation();

      if (confirm("Etes-vous sûr de vouloir supprimer cet exercice ?")) {
        var result = await deleteExerciseAsync(id);

        // If the result is the id of the exercise the deletion worked
        if (result === id) {
          // Refresh the exercice page with the provided parameters
          this.getExercicePage();

          store.dispatch("setSnackbar", {
            title: "Exercice supprimé",
            text: "L'exercice a bien été supprimé.",
            status: "success",
            timeout: 2000,
          });
        }
      }
    },

    // Refresh the exercice page with the provided parameters
    async getExercicePage() {
      // List of federations if admin or super, only one federation if federation manager, discipline manager or coach
      this.availableFederations = await getAllCurrentUserFederationsAsync();
      // List of disciplines if admin, super or federation manager, only one if discipline manager or coach
      this.availableDisciplines = await getAllCurrentUserDisciplinesAsync();
      // List of coaches if admin, super, federation manager or discipline manager, only one if coach
      this.availableCoaches = await getAllCurrentUserCoachesAsync();

      // if current user is related to only one discipline, we wanna grant him the right to see discipline related data
      if (this.availableDisciplines.length === 1) {
        this.selectedDiscipline = this.availableDisciplines[0];

        await this.selectedDisciplineChanged();
      } else {
        // Get the exercises
        this.retrieveExercices();
      }
    },

    // Convert textual value of Exercice property into ExerciceSortType enum
    convertExerciceTextPropertiesIntoEnum(textProp) {
      var result = ExerciceSortType.UNDEFINED;

      switch (textProp) {
        case "id":
          result = ExerciceSortType.ID;
          break;
        case "title":
          result = ExerciceSortType.TITLE;
          break;
        case "destinationType":
          result = ExerciceSortType.DESTINATION;
          break;
        case "email":
          result = ExerciceSortType.EMAIL;
          break;
        case "exerciseType":
          result = ExerciceSortType.TYPENAME;
          break;
        default:
          break;
      }

      return result;
    },

    // on filters changed
    onFiltersChanged() {
      // reset to page 1
      this.options.page = 1;
      this.page = 1;
    },

    // On click to clear the id search input
    onClearIdClicked() {
      this.searchParams.id = null;
      this.onFiltersChanged();
    },

    // On click to clear the title search input
    onClearTitleClicked() {
      this.searchParams.title = null;
      this.onFiltersChanged();
    },

    // On click to clear the destination search input
    onClearDestinationClicked() {
      this.searchParams.destinationType = null;
      this.onFiltersChanged();
    },

    // On click to clear the user search input
    onClearUserClicked() {
      this.searchParams.user = null;
      this.onFiltersChanged();
    },

    // On click to clear the type search input
    onClearTypeClicked() {
      this.searchParams.exerciseType = null;
      this.onFiltersChanged();
    },

    // On click to clear the workform search input
    onClearWorkFormClicked() {
      this.searchParams.workForms = [];
      this.onFiltersChanged();
    },

    //Get the list of WorkForms
    async getWorkFormsForDiscipline(disciplineId) {
      this.allWorkFormsForDiscipline = await getWorkFormForDisciplineAsync(disciplineId);
      if (!this.allWorkFormsForDiscipline) {
        store.dispatch("setSnackbar", {
          title: "Erreur",
          text: "Impossible de récupérer les formes de travail de la discipline auprès du serveur",
          status: "error",
          timeout: 5000,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .v-data-table__empty-wrapper {
  height: 120px;
}
::v-deep .v-select-cust > .v-input__control > .v-input__slot > .v-select__slot > label {
  border: none !important;
}
.v-select-cust {
  max-width: 265px;
  padding-right: 15px;
}
</style>
